/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import Player from "@vimeo/player";
import { trackEvent } from "../../track";
import DOMPurify from "dompurify";

let vimeoPlayer = playerOptions => {
    return {
        showTranscript: false,
        chapters: [],
        text: {},
        linkedVideo: null,
        playerOptions: {
            autoplay: playerOptions?.autoplay || false,
            muted: playerOptions?.muted || false,
            loop: playerOptions?.loop || false,
            controls: playerOptions?.controls || true,
            progress_bar: playerOptions?.progress_bar || true,
            ...(playerOptions?.url && { url: playerOptions.url })
        },
        activeChapter: null,
        showPoster: true,
        playerState: "unplayed",
        player: null,
        init() {
            const url = new URL(DOMPurify.sanitize(document.location));
            const hash = decodeURI(url.hash);

            if (hash !== "") {
                const anchor = this.findAnchorByHash(hash);

                if (anchor) {
                    this.linkedVideo = anchor.dataset.vimeoLink;

                    if (this.$refs.filterGalleryForm) {
                        document.addEventListener("alpine:initialized", () => {
                            anchor.click();
                            this.loadVideo(this.linkedVideo, false);
                        });
                    }

                    if (this.$refs.videoContainer) {
                        this.showPoster = false;
                        const container = this.$refs.videoContainer;

                        window.addEventListener(
                            "load",
                            function() {
                                container.scrollIntoView({ behavior: "smooth", block: "start" });
                            },
                            { once: true }
                        );
                    }
                }
            }
            // Listen for the custom modal event
            window.addEventListener("vimeo-modal-show", e => {
                const vimeoLink = String(e.detail.vimeoLink);
                if (vimeoLink) {
                    this.loadVideo(vimeoLink, false);
                }
            });
        },
        destroyPlayer() {
            if (this.player) {
                const player = this.getPlayer();
                player
                    .destroy()
                    .then(() => {
                        this.player = null;
                        this.playerState = "unplayed";
                        this.$refs.vimeoIframe.removeAttribute("src");
                        this.$refs.vimeoIframe.removeAttribute("data-vimeo-id");
                        this.$refs.vimeoIframe.removeAttribute("data-vimeo-url");
                    })
                    .catch(error => {
                        // eslint-disable-next-line no-console
                        console.warn(error.name);
                    });
            }
        },
        play(retries = 3, currentAttempt = 1) {
            if (!this.player) return Promise.resolve();
            return this.getPlayer()
                .play()
                .then(() => {
                    this.playerState = "playing";
                })
                .catch(error => {
                    // eslint-disable-next-line no-console
                    console.warn(`Play attempt ${currentAttempt} failed`, error.name);
                    if (currentAttempt >= retries) {
                        // eslint-disable-next-line no-console
                        console.warn(`All play attempts failed: ${error}`);
                        throw error;
                    }
                    return new Promise(resolve => setTimeout(resolve, 500)).then(() => {
                        return this.play(retries, currentAttempt + 1);
                    });
                });
        },
        togglePlay() {
            const player = this.getPlayer();

            player.getPaused().then(function(paused) {
                if (paused) {
                    player.play();
                } else {
                    player.pause();
                }
            });
        },
        loadVideo(urlOrId, play) {
            // Reset state before loading new video
            this.playerState = "unplayed";

            if (this.player) {
                this.destroyPlayer();
            }

            // Handle both URLs and video IDs
            if (urlOrId) {
                if (urlOrId.includes("vimeo.com")) {
                    this.playerOptions.url = urlOrId;
                } else {
                    this.playerOptions.url = `https://vimeo.com/${urlOrId}`;
                }
            } else {
                return;
            }
            this.createPlayer(play);
        },
        createPlayer(play) {
            const iframe = this.$refs.vimeoIframe;

            // Clear previous src and data attributes so the Vimeo SDK's auto-init can't pick up a stray bare-ID url
            iframe.removeAttribute("src");
            iframe.removeAttribute("data-vimeo-id");
            iframe.removeAttribute("data-vimeo-url");

            // Get video ID from URL
            const videoId = this.playerOptions.url.match(/vimeo\.com\/(\d+)/)?.[1];

            if (!videoId) {
                //eslint-disable-next-line no-console
                console.warn("No valid video ID found in URL:", this.playerOptions.url);
                this.player = null;
                this.playerState = "unplayed";
                return;
            }

            iframe.setAttribute("data-vimeo-id", videoId);

            // Let Player build the embed URL/src so options stay the single source of truth
            const player = new Player(iframe, {
                id: videoId,
                autoplay: this.playerOptions.autoplay,
                muted: this.playerOptions.muted,
                loop: this.playerOptions.loop,
                controls: this.playerOptions.controls,
                transcript: true // exposes Vimeo's LLM-readable transcript controls in the embed
            });

            // Set player state
            this.player = player;

            player
                .ready()
                .then(() => {
                    this.player = player;
                    this.getChapters(player);
                    this.handleEvents(player);

                    if (play) {
                        this.play();
                    }
                })
                .catch(error => {
                    // eslint-disable-next-line no-console
                    console.warn("Vimeo player error:", error);
                    // Reset player state and iframe on error
                    this.playerState = "unplayed";
                    iframe.removeAttribute("src");
                    iframe.removeAttribute("data-vimeo-id");
                    iframe.removeAttribute("data-vimeo-url");
                    this.destroyPlayer();
                });
        },
        unloadVideo() {
            this.getPlayer()
                .unload()
                .then(() => {
                    this.playerState = "unplayed";
                });
        },
        pause() {
            this.getPlayer().pause();
        },
        //get chapters
        getChapters(player) {
            player.getChapters().then(chapters => {
                this.chapters = chapters;
                this.watchChapterChange(player);
            });
        },
        playByTime(time) {
            const player = new Player(this.$refs.vimeoIframe);
            player.setCurrentTime(time);
        },
        watchChapterChange(player) {
            player.on("chapterchange", () => {
                player.getCurrentChapter().then(chapter => {
                    this.activeChapter = chapter;
                });
            });
        },
        audioTimeMath(time) {
            let minutes = Math.floor(time / 60);
            let seconds = String(`${time % 60}`).padStart(2, "0");
            return `${minutes}:${seconds}`;
        },
        getPlayer() {
            return Alpine.raw(this.player);
        },
        extractVideoId(url) {
            const match = url.match(/vimeo\.com\/(\d+)/);
            return match ? match[1] : null;
        },
        handleEvents(player) {
            const self = this;
            const percentLogger = [];
            player.off("play");
            player.off("pause");
            player.off("ended");
            player.off("timeupdate");

            player.getVideoId().then(videoId => {
                player.on("play", () => {
                    self.playerState = "playing";
                    trackEvent({
                        event: "video",
                        gaVideoEventCategory: "Video",
                        gaVideoEventAction: "Video Play",
                        gaVideoEventLabel: videoId
                    });
                });
                player.on("pause", () => {
                    if (self.playerState == "playing") {
                        self.playerState = "paused";
                        trackEvent({
                            event: "video",
                            gaVideoEventCategory: "Video",
                            gaVideoEventAction: "Pause",
                            gaVideoEventLabel: videoId
                        });
                    }
                });
                player.on("ended", () => {
                    self.playerState = "ended";
                    trackEvent({
                        event: "video",
                        gaVideoEventCategory: "Video",
                        gaVideoEventAction: "Video Complete",
                        gaVideoEventLabel: videoId
                    });
                });
                player.on("timeupdate", function({ percent }) {
                    const currPercent = Math.floor(percent * 100);
                    if (currPercent > 0 && currPercent % 10 === 0 && !percentLogger.includes(currPercent)) {
                        percentLogger.push(currPercent);
                        trackEvent({
                            event: "video",
                            gaVideoEventCategory: "Video",
                            gaVideoEventAction: `Video Progress - ${currPercent}%`,
                            gaVideoEventLabel: videoId
                        });
                    }
                });
            });
        },
        findAnchorByHash(hash) {
            let anchor = this.$el.querySelector(`[data-id-anchor="${hash}"]`);
            if (anchor === null) {
                const strippedHash = hash.substring(1);
                anchor = this.$el.querySelector(`[data-id-anchor="${strippedHash}"]`);
            }
            return anchor;
        }
    };
};

export default vimeoPlayer;
